import React, { useState, useRef, useEffect } from "react";
import "./SelectionSummary.scss";
import CheckboxGroup from "../../components/form/inputs/CheckboxGroup";

// Helper to detect mobile view
const useIsMobile = () => {
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

	React.useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth <= 768);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	return isMobile;
};

const SelectionSummary = ({
	selections,
	showCount = true,
	showReimbursement = true,
	handleSelectionChange,
	variant = "bmw",
	channelSelections = null,
	jlrChannels = null,
	/** When true, render only the total row (e.g. enrollment confirmation). */
	showTotalOnly = false,
	/** When true, render only the formatted monthly total number (no labels or $). */
	showTotalNumberOnly = false,
}) => {
	const isJlr =
		variant === "jlr" && channelSelections != null && jlrChannels != null;
	const jlrCheckedList = isJlr
		? jlrChannels.filter((c) => channelSelections[c.id]?.checked)
		: [];
	const [isExpanded, setIsExpanded] = useState(false); // Default to collapsed
	const [isReimbursementExpanded, setIsReimbursementExpanded] =
		useState(true); // Add this new state
	const isMobile = useIsMobile();
	const selectionSummaryRef = useRef(null); // Ref for the selection summary

	// Hide on scroll or click outside
	useEffect(() => {
		// const handleClickOutside = (event) => {
		// 	// Ignore clicks within the reimbursement section
		// 	const isReimbursementClick = event.target.closest('#id_32');
		// 	if (
		// 		selectionSummaryRef.current &&
		// 		!selectionSummaryRef.current.contains(event.target) &&
		// 		!isReimbursementClick
		// 	) {
		// 		setIsExpanded(false);
		// 	}
		// };
		// const handleScroll = () => {
		// 	if (isMobile) {
		// 		setIsExpanded(false);
		// 	}
		// };
		// // Add event listeners
		// document.addEventListener("click", handleClickOutside);
		// window.addEventListener("scroll", handleScroll);
		// // Cleanup event listeners on component unmount
		// return () => {
		// 	document.removeEventListener("click", handleClickOutside);
		// 	window.removeEventListener("scroll", handleScroll);
		// };
	}, [isMobile]);

	const validSelections = isJlr
		? jlrCheckedList
		: Object.values(selections).filter(
				(value) =>
					value !== "" &&
					value !== "I do not wish to enroll in this program",
			);
	const hasSelections = validSelections.length > 0;

	const jlrBudgetTotal = isJlr
		? jlrCheckedList.reduce((sum, c) => {
				const raw = String(channelSelections[c.id]?.budget ?? "").replace(
					/[^0-9.]/g,
					"",
				);
				const n = parseFloat(raw);
				return sum + (Number.isFinite(n) ? n : 0);
			}, 0)
		: 0;

	// Expand the summary when a selection is made
	useEffect(() => {
		if (hasSelections) {
			setIsExpanded(true);
		}
	}, [hasSelections]);

	const displayMap = {
		"Large: $12,000/month": (
			<div className="size-price-container">
				<span className="size">Large</span>{" "}
				<span className="price">$12,000/Month</span>
			</div>
		),
		"Medium: $8,000/month": (
			<div className="size-price-container">
				<span className="size">Medium</span>{" "}
				<span className="price">$8,000/Month</span>
			</div>
		),
		"Small: $3,000/month": (
			<div className="size-price-container">
				<span className="size">Small</span>{" "}
				<span className="price">$3,000/Month</span>
			</div>
		),
		"Large: $10,000/month": (
			<div className="size-price-container">
				<span className="size">Large</span>{" "}
				<span className="price">$10,000/Month</span>
			</div>
		),
		"Medium: $7,500/month": (
			<div className="size-price-container">
				<span className="size">Medium</span>{" "}
				<span className="price">$7,500/Month</span>
			</div>
		),
		"Small: $2,500/month": (
			<div className="size-price-container">
				<span className="size">Small</span>{" "}
				<span className="price">$2,500/Month</span>
			</div>
		),
		"Large: $3,000/month": (
			<div className="size-price-container">
				<span className="size">Large</span>{" "}
				<span className="price">$3,000/Month</span>
			</div>
		),
		"Medium: $1,500/month": (
			<div className="size-price-container">
				<span className="size">Medium</span>{" "}
				<span className="price">$1,500/Month</span>
			</div>
		),
		"Small: $750/month": (
			<div className="size-price-container">
				<span className="size">Small</span>{" "}
				<span className="price">$750/Month</span>
			</div>
		),
		"Large: TBD": (
			<div className="size-price-container">
				<span className="size">Large</span>{" "}
				<span className="price">TBD</span>
			</div>
		),
		"Medium: TBD": (
			<div className="size-price-container">
				<span className="size">Medium</span>{" "}
				<span className="price">TBD</span>
			</div>
		),
		"Small: TBD": (
			<div className="size-price-container">
				<span className="size">Small</span>{" "}
				<span className="price">TBD</span>
			</div>
		),
		"Large: Custom": (
			<div className="size-price-container">
				<span className="size">Large</span>{" "}
				<span className="price">Custom</span>
			</div>
		),
		"Medium: Custom": <div className="size-price-container"></div>,
		"Small: Custom": (
			<div className="size-price-container">
				<span className="size">Small</span>{" "}
				<span className="price">Custom</span>
			</div>
		),
	};

	const priceMap = {
		"Large: $12,000/month": 12000,
		"Medium: $8,000/month": 8000,
		"Small: $3,000/month": 3000,
		"Large: $10,000/month": 10000,
		"Medium: $7,500/month": 7500,
		"Small: $2,500/month": 2500,
		"Large: $3,000/month": 3000,
		"Medium: $1,500/month": 1500,
		"Small: $750/month": 750,
		"Large: TBD": 0,
		"Medium: TBD": 0,
		"Small: TBD": 0,
		"Custom Budget": 0,
	};

	const bmwPackageTotal = Object.entries(selections)
		.filter(
			([key, value]) =>
				value !== "" &&
				value !== "I do not wish to enroll in this program" &&
				key !== "reimbursement",
		)
		.reduce((total, [_, value]) => total + (priceMap[value] || 0), 0);

	const monthlyTotalAmount = isJlr ? jlrBudgetTotal : bmwPackageTotal;

	if (showTotalNumberOnly) {
		return (
			<div className="selection-summary selection-summary--number-only">
				{monthlyTotalAmount.toLocaleString("en-US", {
					minimumFractionDigits: 0,
					maximumFractionDigits: 2,
				})}
			</div>
		);
	}

	if (showTotalOnly) {
		return (
			<div className="selection-summary selection-summary--total-only">
				<div className="selection-total-wrapper">
					<div className="selection-total">
						<div>
							<p className="dealer-total-title">
								{isJlr ? "TOTAL" : "Total Cost"}
							</p>
							<p className="dealer-total-subtitle">
								{isJlr ? "PER MONTH" : "Total Cost"}
							</p>
						</div>
						<div>
							{`$${monthlyTotalAmount.toLocaleString()}`}
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="selection-summary">
			<div
				onClick={() => setIsExpanded(!isExpanded)}
				className={`selection-summary-header-wrapper ${isMobile ? (isExpanded ? "expanded" : "collapsed") : ""
					}`}
				ref={selectionSummaryRef}>
				<div className="selection-summary-header">
					<h3 className="selection-summary-title">
						YOUR PROGRAM
					</h3>
					{isMobile && (
						<button className="toggle-button">
							<span
								className={`arrow ${isExpanded ? "" : "expanded"}`}></span>
						</button>
					)}
				</div>
			</div>
			{(!isMobile || isExpanded) && (
				<div className="selection-summary-content">
					{!hasSelections ? (
						<div className="no-selections-message">
							No services selected yet.
						</div>
					) : (
						<>
							<div className="selection-summary-items">
								{isJlr &&
									jlrCheckedList.map((c) => (
										<div key={c.id} className="selection-item">
											<p className="selection-item-title">
												{c.label}
											</p>
											<p className="selection-item-price">
												{channelSelections[c.id]?.budget
													? `$${Number(
															channelSelections[c.id].budget,
														).toLocaleString()}`
													: "—"}
											</p>
										</div>
									))}
								{!isJlr && selections.moveUp &&
									selections.moveUp !==
									"I do not wish to enroll in this program" && (
										<div className="selection-item">
											<p className="selection-item-title">
												MOVE UP
											</p>
											<div>
												{displayMap[
													selections.moveUp
												] || selections.moveUp}
											</div>
										</div>
									)}
								{!isJlr && selections.luxury &&
									selections.luxury !==
									"I do not wish to enroll in this program" && (
										<div className="selection-item">
											<p className="selection-item-title">
												LUXURY
											</p>
											<div>
												{displayMap[
													selections.luxury
												] || selections.luxury}
											</div>
										</div>
									)}
								{!isJlr && selections.dynamicInventory &&
									selections.dynamicInventory !==
									"I do not wish to enroll in this program" && (
										<div className="selection-item">
											<p className="selection-item-title">
												DYNAMIC INVENTORY
											</p>
											<div>
												{displayMap[
													selections.dynamicInventory
												] ||
													selections.dynamicInventory}
											</div>
										</div>
									)}
								{!isJlr && selections.leadGen &&
									selections.leadGen !==
									"I do not wish to enroll in this program" && (
										<div className="selection-item">
											<p className="selection-item-title">
												LEAD-GEN
											</p>
											<div>
												{displayMap[
													selections.leadGen
												] || selections.leadGen}
											</div>
										</div>
									)}
								{!isJlr && selections.inlanguage &&
									selections.inlanguage !==
									"I do not wish to enroll in this program" && (
										<div className="selection-item">
											<p className="selection-item-title">
												IN-LANGUAGE
											</p>
											<div>
												{displayMap[
													selections.inlanguage
												] || selections.inlanguage}
											</div>
										</div>
									)}
							</div>
							{showReimbursement && !isJlr && (
								<div
									className="reimbursement-section-wrapper"
									onClick={(e) => e.stopPropagation()}>
									<div className="reimbursement-header">
										<p className="reimbursement-title">
											Simulated Co-op Reimbursement
										</p>
										{!isMobile && (
											<button
												className="reimbursement-toggle"
												onClick={() =>
													setIsReimbursementExpanded(
														!isReimbursementExpanded,
													)
												}>
												<span
													className={`arrow ${isReimbursementExpanded
														? "expanded"
														: ""
														}`}></span>
											</button>
										)}
									</div>
									{(isMobile || isReimbursementExpanded) && (
										<div className="reimbursement-content">
											<div className="reimbursement-section">
												<div className="reimbursement-section-question">
													<CheckboxGroup
														id="id_32"
														name="reimbursement"
														selectedValue={
															selections.reimbursement
														}
														onChange={
															handleSelectionChange
														}
														options={[
															{
																value: "50%",
																label: "50%",
															},
															{
																value: "75%",
																label: "75%",
															},
														]}
													/>
												</div>
												<div className="reimbursement-section-value">
													{selections.reimbursement && (
														<div className="selection-item">
															<div className="reimbursement-amount-value">
																{"-$" +
																	(
																		Object.entries(
																			selections,
																		)
																			.filter(
																				([
																					key,
																					value,
																				]) =>
																					value !==
																					"" &&
																					value !==
																					"I do not wish to enroll in this program" &&
																					key !==
																					"reimbursement",
																			)
																			.reduce(
																				(
																					total,
																					[
																						_,
																						value,
																					],
																				) =>
																					total +
																					(priceMap[
																						value
																					] ||
																						0),
																				0,
																			) *
																		(selections.reimbursement ===
																			"50%"
																			? 0.5
																			: 0.75)
																	).toLocaleString()}{" "}
																
															</div>
														</div>
													)}
												</div>
											</div>
											<p className="reimbursement-section-note">
												*Actual co-op reimbursement
												dependent on available funds.
												Dealers are responsible for
												co-op pacing throughout the
												year. <br /> <br />{" "}
												Reimbursement amounts are set
												for 2026 based on selections
												made by dealers in December.
											</p>

											<div className="selection-total-wrapper">
												<div className="selection-total">
													<div className="total-title">
														Estimated Final Cost
													</div>
													<div className="total-value">
														$
														{(
															Object.entries(
																selections,
															)
																.filter(
																	([
																		key,
																		value,
																	]) =>
																		value !==
																		"" &&
																		value !==
																		"I do not wish to enroll in this program" &&
																		key !==
																		"reimbursement",
																)
																.reduce(
																	(
																		total,
																		[
																			_,
																			value,
																		],
																	) =>
																		total +
																		(priceMap[
																			value
																		] || 0),
																	0,
																) *
															(selections.reimbursement ===
																"50%"
																? 0.5
																: selections.reimbursement ===
																	"75%"
																	? 0.25
																	: 1)
														).toLocaleString()}
													</div>
												</div>
											</div>
										</div>
									)}
								</div>
							)}
							<div className="selection-total-wrapper">
								<div className="selection-total">
									<div>
										<p className="dealer-total-title">{isJlr ? "TOTAL" : "Total Cost"}</p>
										<p className="dealer-total-subtitle">{isJlr ? "PER MONTH" : "Total Cost"}</p>
									
									</div>
									<div>
										$
										{monthlyTotalAmount.toLocaleString()}
									</div>
								</div>
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default SelectionSummary;
